import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface RescheduleInfoModalProps {
  open: boolean;
  onClose: () => void;
  /** When provided, renders the "¿Cuántas tengo yo?" link in the footer. */
  onOpenMyStats?: () => void;
}

interface PolicySection {
  question: string;
  answer: string;
}

const POLICY_SECTIONS: PolicySection[] = [
  {
    question: '¿Qué es un horario de reprogramación?',
    answer:
      'Son franjas horarias reservadas exclusivamente para tus pacientes activos que necesitan reagendar una sesión. No se ofrecen a pacientes nuevos ni se comparten con el equipo de CX para asignaciones.',
  },
  {
    question: '¿Cuántas horas necesito tener configuradas?',
    answer:
      'Debés tener al menos 4 horas de disponibilidad de reprogramación en los próximos 7 días corridos (contados desde mañana). Si tenés menos, recibirás el aviso en tu agenda.',
  },
  {
    question: '¿Cómo se cuentan los 7 días?',
    answer:
      'Se cuentan siempre desde el día siguiente al de hoy hasta 7 días después, sin importar el día de la semana. No es semana calendario.',
  },
  {
    question: '¿Qué pasa si no tengo suficientes horas?',
    answer:
      'Tus pacientes activos pueden no tener opciones disponibles para reagendar, lo que afecta la continuidad de su proceso terapéutico.',
  },
];

export const RescheduleInfoModal: React.FC<RescheduleInfoModalProps> = ({ open, onClose, onOpenMyStats }) => (
  <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
    <DialogContent
      data-testid="reschedule-info-modal"
      className="sm:max-w-[30rem] p-0 overflow-hidden"
    >
      {/* Header */}
      <DialogHeader className="px-6 pt-6 pb-4 mb-0 border-b border-gray-100">
        <DialogTitle className="text-[1.0625rem] font-semibold text-[#4a3d73]">
          Política de horarios de reprogramación
        </DialogTitle>
        <p className="text-[0.8125rem] text-gray-500 mt-1 leading-snug">
          Entendé cómo se calculan y qué pasa si no tenés suficientes horas.
        </p>
      </DialogHeader>

      {/* Body */}
      <div className="px-6 py-5 space-y-5 overflow-y-auto max-h-[60vh]">
        {POLICY_SECTIONS.map((section) => (
          <PolicyCard key={section.question} section={section} />
        ))}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-between items-center gap-3">
        {onOpenMyStats ? (
          <button
            type="button"
            data-testid="reschedule-info-mystats-btn"
            onClick={() => { onClose(); onOpenMyStats(); }}
            className="flex items-center gap-1 text-sm text-[#6D57BC] bg-transparent border-none cursor-pointer hover:underline"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            ¿Cuántas tengo yo?
          </button>
        ) : <span />}
        <button
          type="button"
          onClick={onClose}
          className="px-5 py-2 rounded-full text-sm font-semibold text-white border-none cursor-pointer transition-all hover:brightness-90"
          style={{ background: '#6D57BC' }}
        >
          Entendido
        </button>
      </div>
    </DialogContent>
  </Dialog>
);

const PolicyCard: React.FC<{ section: PolicySection }> = ({ section }) => (
  <div className="flex gap-3 items-start">
    <span className="text-base leading-none mt-0.5 shrink-0" aria-hidden>
      🔁
    </span>
    <div className="min-w-0">
      <p className="text-[0.8125rem] font-semibold text-[#4a3d73] leading-snug mb-1">
        {section.question}
      </p>
      <p className="text-[0.8125rem] text-gray-600 leading-relaxed">
        {section.answer}
      </p>
    </div>
  </div>
);

export default RescheduleInfoModal;
